import React from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Card } from '@/components/ui';
import { NewsletterHistoryStats } from '@/types/newsletter';

interface PublicationMetricChartProps {
  data: NewsletterHistoryStats[];
  title: string;
  dataKey: keyof NewsletterHistoryStats;
  dataKey2?: keyof NewsletterHistoryStats;
  color?: string;
  color2?: string;
  formatValue?: (value: number) => string;
  formatTooltip?: (value: number) => string;
  yAxisWidth?: number;
}

const PublicationMetricChart: React.FC<PublicationMetricChartProps> = ({
  data,
  title,
  dataKey,
  dataKey2,
  color = '#3b82f6',
  color2 = '#10b981',
  formatValue,
  formatTooltip,
  yAxisWidth = 50
}) => {
  // Format date for x-axis
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Default value formatter
  const defaultFormatter = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toFixed(0);
  };

  const valueFormatter = formatValue || defaultFormatter;
  const tooltipFormatter = formatTooltip || valueFormatter;

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="text-xs text-gray-600 mb-1">
            {formatDate(payload[0].payload.snapshotDate)}
          </p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm font-semibold" style={{ color: entry.color }}>
              {entry.name}: {tooltipFormatter(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Get label for data keys
  const getLabel = (key: keyof NewsletterHistoryStats) => {
    const labels: Record<string, string> = {
      activeSubscriptions: 'Active Subs',
      averageOpenRate: 'Open Rate',
      averageClickRate: 'Click Rate',
      totalSent: 'Sent',
      totalDelivered: 'Delivered',
      totalUniqueOpened: 'Opens',
      totalClicked: 'Clicks'
    };
    return labels[key] || key;
  };

  return (
    <Card className="p-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="snapshotDate"
            tickFormatter={formatDate}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            stroke="#e5e7eb"
            tickMargin={8}
          />
          <YAxis
            tickFormatter={valueFormatter}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            stroke="#e5e7eb"
            width={yAxisWidth}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey={dataKey as string}
            stroke={color}
            strokeWidth={2}
            dot={false}
            name={getLabel(dataKey)}
            animationDuration={500}
          />
          {dataKey2 && (
            <Line
              type="monotone"
              dataKey={dataKey2 as string}
              stroke={color2}
              strokeWidth={2}
              dot={false}
              name={getLabel(dataKey2)}
              animationDuration={500}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};

export default PublicationMetricChart;
